import React, { useRef } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StarRatingProps {
  value: number;
  /** Sem onChange, renderiza só leitura. */
  onChange?: (value: number) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' };

const StarRating: React.FC<StarRatingProps> = ({ value, onChange, size = 'md', label }) => {
  const { t } = useTranslation();
  const current = Math.max(1, Math.min(5, value || 5));
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  if (!onChange) {
    return (
      <div className="flex items-center gap-0.5" role="img" aria-label={t('testimonialsUi.starsLabel', { count: current })}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} className={`${sizes[size]} ${n <= current ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />
        ))}
      </div>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let next = current;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(5, current + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(1, current - 1);
    else return;
    e.preventDefault();
    onChange(next);
    buttonsRef.current[next - 1]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className="flex items-center gap-1" onKeyDown={handleKeyDown}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          ref={(el) => { buttonsRef.current[n - 1] = el; }}
          type="button"
          role="radio"
          aria-checked={n === current}
          aria-label={t('testimonialsUi.ratingStar', { count: n })}
          tabIndex={n === current ? 0 : -1}
          onClick={() => onChange(n)}
          className="p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-transform hover:scale-110"
        >
          <Star className={`${sizes[size]} ${n <= current ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
};

export default StarRating;
